/**
 * @file RedirectRow.tsx
 * @description Строка переадресации (phase19.16, партия Р3в, v0.19.64;
 * SB131; uiux-r3 §3.5): «Откуда → Куда (название и адрес) · откуда взялась ·
 * ⋯». С 768 — строка таблицы, до 768 — карточкой: «/about → О нас» / «авто ·
 * 19.10» / ⋯ 48 px. Не действующее правило — пометкой «не действует: …»
 * тоном «внимание». «⋯»: Изменить, Проверить, Удалить; без права настроек —
 * только «Проверить».
 */

import { Pencil, SearchCheck, Trash2 } from "lucide-react";

import { RowActionsMenu, type RowAction } from "../../ui/RowActionsMenu";
import type { SiteRedirectItem } from "../../../lib/api-site-redirects";
import { redirectSourceText, redirectStatusText, redirectTargetText } from "../../../lib/site-seo/redirect-view";

interface Props {
  item: SiteRedirectItem;
  canEdit: boolean;
  onEdit: (item: SiteRedirectItem) => void;
  onProbe: (path: string) => void;
  onRemove: (item: SiteRedirectItem) => void;
}

/** Колонки строки с 768: откуда · куда · откуда взялась · ⋯. */
export const REDIRECT_ROW_GRID = "md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_minmax(0,1fr)_3rem] md:items-center md:gap-4";

export function RedirectRow({ item, canEdit, onEdit, onProbe, onRemove }: Props) {
  const status = redirectStatusText(item);
  const actions: RowAction[] = [
    ...(canEdit ? [{ key: "edit", label: "Изменить", icon: Pencil, run: () => onEdit(item) }] : []),
    { key: "probe", label: "Проверить", icon: SearchCheck, run: () => onProbe(item.fromDisplay) },
    ...(canEdit ? [{ key: "remove", label: "Удалить", icon: Trash2, danger: true, run: () => onRemove(item) }] : []),
  ];
  return (
    <li className={`flex items-start gap-2 px-4 py-3 ${REDIRECT_ROW_GRID}`} data-testid="redirect-row">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 md:contents">
        <span className="type-body min-w-0 break-all font-mono text-on-surface">{item.fromDisplay}</span>
        <span className="type-body min-w-0 break-words text-on-surface">
          <span aria-hidden className="text-on-surface-variant">→ </span>
          <span className="sr-only">ведёт на </span>
          {redirectTargetText(item.target)}
          {status ? <span className="type-caption block text-warning">{status}</span> : null}
        </span>
        <span className="type-caption min-w-0 text-on-surface-variant">{redirectSourceText(item)}</span>
      </div>
      <div className="shrink-0">
        <RowActionsMenu actions={actions} title={`Переадресация ${item.fromDisplay}`} ariaLabel={`Действия: ${item.fromDisplay}`} />
      </div>
    </li>
  );
}
